import React, { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as EyeActiveIcon } from '../../../../assets/icons/dashboard/eye-icon-active.svg';
import { ReactComponent as EyeIcon } from '../../../../assets/icons/dashboard/eye-icon.svg';
import { ReactComponent as PlusActiveIcon } from '../../../../assets/icons/dashboard/plus-active-icon.svg';
import { ReactComponent as PlusIcon } from '../../../../assets/icons/dashboard/plus-icon.svg';
import CardsBgImageLight from '../../../../assets/images/dashboard/cards-bg.png';
import ChargePointImage from '../../../../assets/images/dashboard/charge-point.png';
import ConnecterImage from '../../../../assets/images/dashboard/connecter.png';
import CardsBgImageDark from '../../../../assets/images/dashboard/dashboardCard-dark.png';
import DefectImage from '../../../../assets/images/dashboard/defect.png';
import StationImage from '../../../../assets/images/dashboard/station.png';
import { brandImage } from '../../../../config/branding';
import ThemeContext from '../../../../contexts/theme-context/ThemeContext';
import { useAppDispatch } from '../../../../redux/hooks';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import dashboard from '../../../../utils/api/partner/dashboard';

// Define the type for the function fetching data
type FetchFunction = () => Promise<{ status: number; data: Record<string, number> }>;

// Define the type for the card count state
interface CardCount {
  totalConnectors?: number;
  totalChargingStations?: number;
  totalChargingPoints?: number;
  totalFaultedConnectors?: number;
}

/**
 * Dashboard cards component with background scrolling image
 */
function DashboardCards() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useContext(ThemeContext);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [cardCount, setCardCount] = useState({
    totalConnectors: 0,
    totalChargingStations: 0,
    totalChargingPoints: 0,
    totalFaultedConnectors: 0,
  });

  /**
   * Function  to handle navigation
   * @param {any} url - The url
   */
  const handleIconClick = (url: string) => {
    navigate(url);
  };

  const cardsData = [
    {
      count: cardCount.totalConnectors,
      title: 'connectors',
      cardImage: ConnecterImage,
      clickButtons: [
        {
          svgIcon: <EyeIcon />,
          svgIconOnHover: <EyeActiveIcon />,
          onClickUrl: '/ev-admin/operations/chargers',
        },
      ],
    },
    {
      count: cardCount.totalChargingStations,
      title: 'chargingStations',
      cardImage: StationImage,
      clickButtons: [
        {
          svgIcon: <PlusIcon />,
          svgIconOnHover: <PlusActiveIcon />,
          onClickUrl: '/ev-admin/station-management/add',
        },
        {
          svgIcon: <EyeIcon />,
          svgIconOnHover: <EyeActiveIcon />,
          onClickUrl: '/ev-admin/station-management/approved',
        },
      ],
    },
    {
      count: cardCount.totalChargingPoints,
      title: 'chargingPoints',
      cardImage: ChargePointImage,
      clickButtons: [
        {
          svgIcon: <PlusIcon />,
          svgIconOnHover: <PlusActiveIcon />,
          onClickUrl: '/ev-admin/operations/chargers/add',
        },
        {
          svgIcon: <EyeIcon />,
          svgIconOnHover: <EyeActiveIcon />,
          onClickUrl: '/ev-admin/operations/chargers',
        },
      ],
    },
    {
      count: cardCount.totalFaultedConnectors,
      title: 'defects',
      cardImage: DefectImage,
      clickButtons: [
        {
          svgIcon: <EyeIcon />,
          svgIconOnHover: <EyeActiveIcon />,
          onClickUrl: '/ev-admin/operations/chargers?connectorStatus=Faulted',
        },
      ],
    },
  ];

  /**
   * Generic function to fetch and set card count
   * @param {any} fetchFunction - The fetchFunction
   * @param {any} key - The key
   */
  const fetchAndSetCardCount = async (
    fetchFunction: FetchFunction,
    key: keyof CardCount,
  ): Promise<void> => {
    try {
      const response = await fetchFunction();
      if (response.status === 200) {
        const value = response.data[key];
        setCardCount((previousValue) => ({
          ...previousValue,
          [key]: value,
        }));
      }
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  useEffect(() => {
    /**
     * Function to fetch card details one by one
     */
    const getCardsCount = async () => {
      const fetchFunctions = [
        {
          fetch: dashboard.getConnectorCount,
          key: 'totalConnectors' as keyof CardCount,
        },
        {
          fetch: dashboard.getStationCount,
          key: 'totalChargingStations' as keyof CardCount,
        },
        {
          fetch: dashboard.getChargingPointCount,
          key: 'totalChargingPoints' as keyof CardCount,
        },
        {
          fetch: dashboard.getDefectCount,
          key: 'totalFaultedConnectors' as keyof CardCount,
        },
      ];

      // Execute all fetch operations using `Promise.allSettled`
      const results = await Promise.allSettled(
        fetchFunctions.map(({ fetch, key }) => fetchAndSetCardCount(fetch, key)),
      );

      // Log results for debugging (optional)
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(`Error in API call for ${fetchFunctions[index].key}:`, result.reason);
        }
      });
    };

    getCardsCount();
  }, []);

  const cardsBgImage = theme === 'light' ? CardsBgImageLight : CardsBgImageDark;

  return (
    <div className="dashboard-cards-wrap-partner">
      {/* To-do - Animation not matched with design */}
      <div className="dashboard-cards-bg">
        <img src={cardsBgImage} alt="" className="dashboard-cards-bg-image" />
        <img src={cardsBgImage} alt="" className="dashboard-cards-bg-image" />
      </div>
      <div className="dashboard-cards-container">
        {cardsData.map((card, index) => (
          <div className="each-card-container">
            <div className="card-details">
              <h1>{card.count}</h1>
              <p>{t(card.title)}</p>
            </div>
            <div
              className={`card-image-wrap ${hoverIndex === index && 'hovered'}`}
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}>
              <img src={card.cardImage} alt="" className="card-image" />
              <div className="card-icons-wrap">
                {card.clickButtons.map((clickButton) => (
                  <div
                    className="card-icon-container"
                    role="presentation"
                    onClick={() => handleIconClick(clickButton.onClickUrl)}>
                    <span className="in-active-icon">{clickButton.svgIcon}</span>
                    <span className="active-icon">{clickButton.svgIconOnHover}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      <img src={brandImage.cardsCar} alt="" className="dashboard-cards-car-image" />
    </div>
  );
}

export default DashboardCards;
